// Client mirror of supabase/functions/generate-progress-claim/hire-claimability.ts
// (the app does not import across into supabase/functions; parity is enforced by
// the deno test in that directory).
//
// SEQ-145 Amendment 1 §2.3: billing_status never transitions to 'paid' in
// production data, so claimability requires the paid boolean to be false. A
// paid row must never be selectable, whatever its status reads.

export interface HireClaimabilityRow {
  paid?: boolean | null;
  billing_status?: string | null;
  claim_report_id?: string | null;
}

export function isHireRowClaimable(
  row: HireClaimabilityRow,
  existingReportId: string | null,
): boolean {
  if (row.paid) return false;
  // SEQ-216: a row attached to a DIFFERENT report is already claimed there,
  // whatever billing_status reads (sync reset 'pending' onto 16 claimed rows
  // before SEQ-169). Selecting it would count it twice in A.
  if (row.claim_report_id && row.claim_report_id !== existingReportId) {
    return false;
  }
  const status = row.billing_status || "pending";
  if (status === "pending") return true;
  if (row.claim_report_id && row.claim_report_id === existingReportId) {
    return status === "included" || status === "paid";
  }
  return false;
}

// SEQ-218: re-claiming an unpaid month that an EARLIER claim already claimed.
// isHireRowClaimable is unchanged: such a row is still "foreign-claim" for the
// normal selection. This is a separate, opt-in predicate (the modal sends the
// rows in their own request field, reclaimHireKeys).

export interface HireReclaimRow extends HireClaimabilityRow {
  amount?: number | string | null;
}

export interface HireReclaimReport {
  id?: string | null;
  claim_month?: number | string | null;
  claim_year?: number | string | null;
}

/**
 * True only when the row is unpaid, has an amount above zero and is attached
 * to a report from a period strictly BEFORE the claim being generated. A paid
 * row, a row of the same or a later claim, an unattached row and a $0 row are
 * never offered.
 */
export function isHireRowReclaimable(
  row: HireReclaimRow,
  claimYear: number,
  claimMonth: number,
  reports: readonly HireReclaimReport[],
): boolean {
  if (row.paid) return false;
  if (!(Number(row.amount) > 0)) return false;
  const owner = row.claim_report_id;
  if (!owner) return false;
  const report = reports.find((r) => r.id === owner);
  if (!report) return false;
  const y = Number(report.claim_year);
  const m = Number(report.claim_month);
  return y < claimYear || (y === claimYear && m < claimMonth);
}

export function hireTupleKey(
  hireRegisterId: string,
  billingMonth: number,
  billingYear: number,
): string {
  return `${hireRegisterId}|${Number(billingMonth)}|${Number(billingYear)}`;
}

/**
 * The re-claimed rows a saved claim froze: tuple key -> the report the row
 * was re-claimed from. Read from claimed_set.hire_billing, so a regeneration
 * of the new claim knows which of its rows are re-claims (the row itself only
 * says it is attached to the new claim; no column holds the origin).
 */
export function readReclaimOrigins(
  claimedSet: unknown,
  validAgainst?: {
    reports: readonly HireReclaimReport[];
    claimYear: number;
    claimMonth: number;
  },
): Map<string, string> {
  const out = new Map<string, string>();
  const items = (claimedSet as { hire_billing?: unknown } | null)?.hire_billing;
  if (!Array.isArray(items)) return out;
  for (const item of items) {
    const origin = item?.reclaimed_from_report_id;
    const register = item?.hire_register_id;
    if (typeof origin !== "string" || !origin) continue;
    if (typeof register !== "string" || !register) continue;
    // A frozen origin is only believed when it is one of this project's claims
    // and from a period strictly before the claim being generated.
    if (validAgainst) {
      const report = validAgainst.reports.find((r) => r.id === origin);
      if (!report) continue;
      const y = Number(report.claim_year);
      const m = Number(report.claim_month);
      const earlier = y < validAgainst.claimYear ||
        (y === validAgainst.claimYear && m < validAgainst.claimMonth);
      if (!earlier) continue;
    }
    out.set(
      hireTupleKey(register, item.billing_month, item.billing_year),
      origin,
    );
  }
  return out;
}

/**
 * A regenerated claim that UNTICKS a re-claimed row hands it back to the
 * report it came from, so for A (hire claimed to date) the row is still
 * claimed elsewhere. Rows still attached to the claim being regenerated,
 * unpaid, not selected and frozen as a re-claim are re-pointed at their origin.
 */
export function reassignUnselectedReclaims<
  R extends {
    id?: string | null;
    claim_report_id?: string | null;
    paid?: boolean | null;
    hire_register_id?: string | null;
    billing_month?: number | string | null;
    billing_year?: number | string | null;
  },
>(
  rows: readonly R[],
  selectedIds: ReadonlySet<string>,
  origins: ReadonlyMap<string, string>,
  existingReportId: string | null,
): R[] {
  if (!existingReportId || origins.size === 0) return [...rows];
  return rows.map((row) => {
    if (row.claim_report_id !== existingReportId) return row;
    if (row.paid) return row;
    if (row.id && selectedIds.has(row.id)) return row;
    if (!row.hire_register_id) return row;
    const origin = origins.get(
      hireTupleKey(
        row.hire_register_id,
        Number(row.billing_month),
        Number(row.billing_year),
      ),
    );
    return origin ? { ...row, claim_report_id: origin } : row;
  });
}
